import React, { useEffect, useState } from 'react';
import { X, MessageCircle } from 'lucide-react';

const ChatMessageNotification = ({ 
  message, 
  sender, 
  onClose, 
  onClick,
  duration = 3000 
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(100);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Start progress countdown
    const interval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev - (100 / (duration / 50));
        return newProgress <= 0 ? 0 : newProgress;
      });
    }, 50);

    // Auto close after duration
    const timeout = setTimeout(() => {
      handleClose();
    }, duration);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [duration]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, 300); // Match fade animation
  };

  const handleClick = () => {
    onClick?.();
    handleClose();
  };

  const getSenderInitials = () => {
    if (!sender?.name) return 'U';
    const names = sender.name.split(' ');
    if (names.length > 1) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return sender.name.substring(0, 2).toUpperCase();
  };

  const getSenderGradient = () => {
    const gradients = [
      'from-blue-400 to-blue-600',
      'from-purple-400 to-purple-600',
      'from-green-400 to-green-600',
      'from-orange-400 to-orange-600',
      'from-pink-400 to-pink-600',
      'from-indigo-400 to-indigo-600',
      'from-teal-400 to-teal-600',
      'from-red-400 to-red-600',
    ];
    
    const hash = sender?.id?.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) || 0;
    return gradients[hash % gradients.length];
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`max-w-sm w-full transform transition-all duration-300 ${
        isClosing ? 'opacity-0 translate-x-2 scale-95' : 'opacity-100 translate-x-0 scale-100'
      }`}
    >
      <div 
        onClick={handleClick}
        className="bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden cursor-pointer hover:shadow-2xl transition-shadow duration-200 min-h-[80px]"
      >
        {/* Progress Bar */}
        <div className="h-1 bg-gray-100 relative overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-75 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Notification Content */}
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Avatar */}
            <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getSenderGradient()} flex items-center justify-center text-white font-semibold text-sm shadow-md flex-shrink-0`}>
              {getSenderInitials()}
            </div>

            {/* Message Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-semibold text-gray-900 truncate">
                  {sender?.name || 'Unknown User'}
                </h4>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-100"
                >
                  <X size={14} />
                </button>
              </div>
              
              <p className="text-sm text-gray-600 leading-relaxed" style={{ 
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}>
                {message}
              </p>
              
              <div className="flex items-center gap-1 mt-2 text-xs text-blue-600">
                <MessageCircle size={12} />
                <span>Click to open chat</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatMessageNotification;